import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { protestService, getImageUrl } from '../services/api';
import { useAuthStore } from '../store/useAuthStore';
import PhotoGallery from '../components/PhotoGallery';
import EditProtestForm from '../components/EditProtestForm';
import SourcesPanel from '../components/SourcesPanel';
import StoriesPanel from '../components/StoriesPanel';
import styles from './ProtestDetail.module.css';

const ORIENTATION_LABELS = {
    government_supporter: 'مؤيد للحكومة',
    government_opponent:  'معارض للحكومة',
    sectarian:            'طائفي',
    separatist:           'انفصالي',
    neutral_undefined:    'محايد/غير محدد',
    riots:                'أعمال شغب',
};

export default function ProtestDetail() {
    const { id }     = useParams();
    const navigate   = useNavigate();
    const user       = useAuthStore((s) => s.user);

    const [protest,  setProtest]  = useState(null);
    const [loading,  setLoading]  = useState(true);
    const [error,    setError]    = useState(null);
    const [editing,  setEditing]  = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => { load(); }, [id]);

    const load = async () => {
        try {
            setLoading(true);
            const res = await protestService.get(id);
            setProtest(res.data);
        } catch {
            setError('تعذر تحميل بيانات الاحتجاج');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!window.confirm('هل أنت متأكد من الحذف؟')) return;
        setDeleting(true);
        try {
            await protestService.delete(id);
            navigate('/protests');
        } catch {
            alert('فشل الحذف');
            setDeleting(false);
        }
    };

    if (loading) return <div className={styles.state}>جاري التحميل...</div>;
    if (error)   return <div className={styles.state}>{error}</div>;
    if (!protest) return null;

    const canEdit = (user?.role === 'super_admin'
        || user?.role === 'free_user'
        || user?.id === protest.user_id)
        && user?.role !== 'monitor';

    const photos = [
        ...(protest.photos ?? []).map((p) => p.photo_url),
        ...((protest.photos?.length === 0 && protest.photo_path) ? [getImageUrl(protest.photo_path)] : []),
    ].filter(Boolean);

    const dateStr = protest.protest_date
        ? new Date(protest.protest_date).toLocaleDateString('ar-SY', { year: 'numeric', month: 'long', day: 'numeric' })
        : '—';

    const locationTitle = protest.governorate?.name
        ? protest.region?.name
            ? `${protest.governorate.name} — ${protest.region.name}`
            : protest.governorate.name
        : protest.region?.name || 'غير محدد';

    if (editing) {
        return (
            <div className={styles.container}>
                <EditProtestForm
                    protest={protest}
                    onSave={(updated) => { setProtest(updated); setEditing(false); }}
                    onCancel={() => setEditing(false)}
                />
            </div>
        );
    }

    return (
        <div className={`${styles.container} text-ar`}>

            {/* ── Header ── */}
            <div className={styles.header}>
                <button className={styles.backBtn} onClick={() => navigate(-1)}>← العودة</button>

                <div className={styles.titleRow}>
                    <h1 className={styles.title}>{locationTitle}</h1>
                    {canEdit && (
                        <div className={styles.headerActions}>
                            <button className={styles.editBtn} onClick={() => setEditing(true)}>✏ تعديل</button>
                            <button className={styles.deleteBtn} onClick={handleDelete} disabled={deleting}>
                                {deleting ? '...' : '🗑 حذف'}
                            </button>
                        </div>
                    )}
                </div>

                <div className={styles.chips}>
                    {protest.protest_type && (
                        <span className={`${styles.chip} ${styles.chipType}`}>{protest.protest_type}</span>
                    )}
                    <span className={styles.chip}>📅 {dateStr}</span>
                    {protest.estimated_participants > 0 && (
                        <span className={`${styles.chip} ${styles.chipCount}`}>
                            👥 {protest.estimated_participants.toLocaleString('ar-SY')} مشارك
                        </span>
                    )}
                    {protest.verified && (
                        <span className={`${styles.chip} ${styles.chipVerified}`}>✓ مرصود ميدانياً</span>
                    )}
                </div>
            </div>

            {/* ── Photos ── */}
            {photos.length > 0 && (
                <section className={styles.section}>
                    <h2 className={styles.sectionTitle}>الصور ({photos.length})</h2>
                    <PhotoGallery photos={photos} />
                </section>
            )}

            {/* ── Info grid ── */}
            <section className={styles.section}>
                <h2 className={styles.sectionTitle}>تفاصيل الاحتجاج</h2>
                <div className={styles.infoGrid}>
                    <InfoRow label="المحافظة"    value={protest.governorate?.name} />
                    <InfoRow label="المنطقة"     value={protest.region?.name} />
                    <InfoRow label="التاريخ"     value={dateStr} />
                    <InfoRow label="نوع الحدث"   value={protest.protest_type} />
                    <InfoRow label="عدد المشاركين"
                        value={protest.estimated_participants > 0
                            ? protest.estimated_participants.toLocaleString('ar-SY') + ' مشارك'
                            : null} />
                    <InfoRow label="التصنيف الرئيسي"  value={protest.category?.name} />
                    <InfoRow label="التصنيف الفرعي"   value={protest.sub_category?.name} />
                    <InfoRow label="التوجه السياسي"
                        value={protest.political_orientation
                            ? (ORIENTATION_LABELS[protest.political_orientation] ?? protest.political_orientation)
                            : null} />
                    <InfoRow label="الرصد الميداني"
                        value={protest.verified != null
                            ? (protest.verified ? 'مرصود ميدانياً ✓' : 'غير مرصود')
                            : null}
                        accent={protest.verified} />
                    <InfoRow label="أُدخل بواسطة"  value={protest.user?.name} />
                    <InfoRow label="تاريخ الإدخال"
                        value={protest.created_at
                            ? new Date(protest.created_at).toLocaleString('ar-SY')
                            : null} />
                    {protest.updated_at !== protest.created_at && (
                        <InfoRow label="آخر تحديث"
                            value={new Date(protest.updated_at).toLocaleString('ar-SY')} />
                    )}
                </div>
            </section>

            {/* ── Demands ── */}
            {protest.demands && (
                <section className={styles.section}>
                    <h2 className={styles.sectionTitle}>المطالب والأهداف</h2>
                    <p className={styles.demands}>{protest.demands}</p>
                </section>
            )}

            {/* ── Source URL ── */}
            {protest.source_url && (
                <section className={styles.section}>
                    <h2 className={styles.sectionTitle}>رابط المصدر</h2>
                    <a
                        href={protest.source_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles.sourceLink}
                    >
                        🔗 {protest.source_url}
                    </a>
                </section>
            )}

            {/* ── Map ── */}
            {protest.latitude && protest.longitude && (
                <section className={styles.section}>
                    <h2 className={styles.sectionTitle}>الموقع الجغرافي</h2>
                    <a
                        className={styles.mapLink}
                        href={`https://www.google.com/maps?q=${protest.latitude},${protest.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        📍 فتح على الخريطة ({Number(protest.latitude).toFixed(4)}, {Number(protest.longitude).toFixed(4)})
                    </a>
                    {protest.geo_source && (
                        <span className={styles.geoSource}>المصدر: {protest.geo_source === 'exif' ? 'بيانات EXIF للصورة' : protest.geo_source}</span>
                    )}
                </section>
            )}

            {/* ── Sources ── */}
            <section className={styles.section}>
                <SourcesPanel protestId={protest.id} canEdit={canEdit} />
            </section>

            {/* ── Stories ── */}
            <section className={styles.section}>
                <StoriesPanel protestId={protest.id} canEdit={canEdit} />
            </section>

        </div>
    );
}

function InfoRow({ label, value, accent }) {
    if (!value) return null;
    return (
        <div className={styles.infoRow}>
            <span className={styles.infoLabel}>{label}</span>
            <span className={`${styles.infoValue} ${accent ? styles.accentValue : ''}`}>{value}</span>
        </div>
    );
}
